import { useCallback, useMemo, useState } from 'react'

import { GeneratorCategory } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router'

import {
  Checkbox,
  HStack,
  PrimaryButton,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular } from '@enechain/ecloud-icons'

import { CompactAlert } from '~/components/common/CompactAlert'
import { DeleteConfirmDialog } from '~/components/common/DeleteConfirmDialog'
import { DsSelect } from '~/components/common/DsSelect'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { RegionLoading } from '~/components/common/RegionLoading'
import {
  convertListPlantMastersResponse,
  useListPlantMasters,
} from '~/hooks/useListPlantMasters'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import type { DeleteErrorInfo } from '~/pages/AdminOcctoMaster/shell/types'
import { MasterFilterToolbar } from '~/pages/AdminOcctoMaster/tabs/_common/components/MasterFilterToolbar'
import { isRetriableDeleteError } from '~/pages/AdminOcctoMaster/tabs/_common/deleteError'
import { mapMasterGetError } from '~/pages/AdminOcctoMaster/tabs/_common/getError'
import { useTodayJst } from '~/pages/AdminOcctoMaster/tabs/_common/hooks/useTodayJst'
import { CreatePlantUnitModal } from '~/pages/AdminPlantUnitMaster/components/CreatePlantUnitModal'
import {
  type PlantCodeSource,
  PlantUnitTable,
} from '~/pages/AdminPlantUnitMaster/components/PlantUnitTable'
import { useDeletePlantUnit } from '~/pages/AdminPlantUnitMaster/hooks/useDeletePlantUnit'
import { useListGenerationSites } from '~/pages/AdminPlantUnitMaster/hooks/useListGenerationSites'
import { useListPlantUnits } from '~/pages/AdminPlantUnitMaster/hooks/useListPlantUnits'
import { PLANT_UNIT_MASTER_FILTER_STORAGE_KEY } from '~/pages/AdminPlantUnitMaster/models/filterStorage'
import { generatorCategoryLabel } from '~/pages/AdminPlantUnitMaster/models/labels'
import { convertListGenerationSitesResponse } from '~/pages/AdminPlantUnitMaster/models/siteRows'
import {
  DEFAULT_UNIT_FILTER,
  GENERATOR_CATEGORY_OPTIONS,
  type PlantUnitRow,
  type UnitFilter,
  type UnitSort,
  convertListPlantUnitsResponse,
  countAreaMismatches,
  countMasterGaps,
  flattenGroups,
  groupUnitsBySite,
  matchesUnitFilter,
} from '~/pages/AdminPlantUnitMaster/models/unitRows'
import {
  PLANT_UNIT_ERROR_KEY_PREFIX,
  mapPlantUnitDeleteError,
} from '~/pages/AdminPlantUnitMaster/models/unitServerError'
import { routePaths } from '~/routes/routePaths'

/**
 * 電源ユニット画面の「電源ユニット」タブ = 発電所 (サイト) ごとにまとめたユニットの一覧・絞り込み・登録・削除。
 * ユニット行のクリックと行末の詳細で詳細ページへ移る。
 */
export const PlantUnitTab = (): React.ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const showSuccessToast = useSuccessToast()
  const today = useTodayJst()

  const units = useListPlantUnits()
  const sites = useListGenerationSites()
  const plantMasters = useListPlantMasters()
  const { mutateAsync: deleteUnit, isPending: isDeleting } =
    useDeletePlantUnit()

  const [filter, setFilter] = useState<UnitFilter>(DEFAULT_UNIT_FILTER)
  const [sort, setSort] = useState<UnitSort | undefined>(undefined)
  const [collapsedSiteIds, setCollapsedSiteIds] = useState<ReadonlySet<string>>(
    new Set(),
  )
  const [isCreateOpen, setCreateOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<PlantUnitRow | undefined>(
    undefined,
  )
  const [deleteError, setDeleteError] = useState<DeleteErrorInfo | undefined>(
    undefined,
  )

  const unitRows = useMemo(
    () =>
      units.data !== undefined ? convertListPlantUnitsResponse(units.data) : [],
    [units.data],
  )
  const siteRows = useMemo(
    () =>
      sites.data !== undefined
        ? convertListGenerationSitesResponse(sites.data)
        : [],
    [sites.data],
  )
  const plantRows = useMemo(
    () =>
      plantMasters.data !== undefined
        ? convertListPlantMastersResponse(plantMasters.data)
        : [],
    [plantMasters.data],
  )

  const masters = useMemo(
    () => ({
      sites: new Map(
        siteRows.map((site) => [
          site.id,
          { siteName: site.siteName, areaCode: site.areaCode },
        ]),
      ),
      plantCodes: new Map(
        plantRows.map((plant) => [plant.id, plant.plantCode]),
      ),
    }),
    [siteRows, plantRows],
  )
  const siteAudits = useMemo(
    () => new Map(siteRows.map((site) => [site.id, site])),
    [siteRows],
  )
  const siteAreas = useMemo(
    () => new Map(siteRows.map((site) => [site.id, site.areaCode])),
    [siteRows],
  )
  const plantSources = useMemo(
    (): ReadonlyMap<string, PlantCodeSource> =>
      new Map(
        plantRows.map((plant) => [
          plant.id,
          {
            plantCode: plant.plantCode,
            validFrom: plant.validFrom,
            validTo: plant.validTo,
          },
        ]),
      ),
    [plantRows],
  )

  // 「マスタ紐付け未完了のみ」以外の絞り込みを掛けた範囲 (押した時に出る件数と一致させる)
  const withoutMissingOnly = useMemo(
    () =>
      unitRows.filter((unit) =>
        matchesUnitFilter(
          unit,
          { ...filter, onlyMissingMaster: false },
          today,
          masters,
        ),
      ),
    [unitRows, filter, today, masters],
  )
  const gapCounts = useMemo(
    () => countMasterGaps(withoutMissingOnly),
    [withoutMissingOnly],
  )
  const mismatchCount = useMemo(
    () => countAreaMismatches(withoutMissingOnly),
    [withoutMissingOnly],
  )
  const visibleUnits = useMemo(
    () =>
      unitRows.filter((unit) =>
        matchesUnitFilter(unit, filter, today, masters),
      ),
    [unitRows, filter, today, masters],
  )
  const groups = useMemo(
    () => groupUnitsBySite(visibleUnits, masters, sort),
    [visibleUnits, masters, sort],
  )
  const rows = useMemo(
    () => flattenGroups(groups, collapsedSiteIds),
    [groups, collapsedSiteIds],
  )

  const hasGaps = gapCounts.plantCode + gapCounts.bg > 0
  const hasNotice = hasGaps || mismatchCount > 0
  const canFilterMissing = hasNotice && !filter.onlyMissingMaster
  const allCollapsed =
    groups.length > 0 &&
    groups.every((group) => collapsedSiteIds.has(group.siteId))

  const toggleGroup = useCallback((siteId: string): void => {
    setCollapsedSiteIds((prev) => {
      const next = new Set(prev)
      if (next.has(siteId)) next.delete(siteId)
      else next.add(siteId)
      return next
    })
  }, [])
  const toggleAllGroups = (): void => {
    setCollapsedSiteIds(
      allCollapsed ? new Set() : new Set(groups.map((group) => group.siteId)),
    )
  }

  const openDetail = useCallback(
    (unit: PlantUnitRow): void => {
      void navigate(
        generatePath(routePaths.adminPlantUnitDetail.path, { id: unit.id }),
      )
    },
    [navigate],
  )

  const closeDelete = (): void => {
    setDeleteTarget(undefined)
    setDeleteError(undefined)
  }
  const confirmDelete = async (): Promise<void> => {
    if (deleteTarget === undefined) return
    try {
      await deleteUnit({
        id: deleteTarget.id,
        expectedUpdatedAt: deleteTarget.updatedAt,
      })
      closeDelete()
      showSuccessToast({ title: t('common:toast.deleted') })
    } catch (err) {
      setDeleteError({
        message: mapPlantUnitDeleteError(t, err),
        retriable: isRetriableDeleteError(err),
      })
    }
  }

  const firstError =
    units.isError && units.data === undefined ? units.error : undefined
  const errorLabel =
    firstError !== undefined
      ? mapMasterGetError(t, firstError, PLANT_UNIT_ERROR_KEY_PREFIX)
      : undefined
  const showInitialLoading = units.isLoading && units.data === undefined

  const f = (key: string): string => t(`page:adminPlantUnitMaster.unit.${key}`)
  // 注意の見出し: 件数のある項目を「・」で並べる。エリア不一致は AC-OMN-017 の文言。
  const noticeTitle = [
    gapCounts.plantCode > 0 &&
      t('page:adminPlantUnitMaster.unit.missing.partPlantCode', {
        count: gapCounts.plantCode,
      }),
    gapCounts.bg > 0 &&
      t('page:adminPlantUnitMaster.unit.missing.partBg', {
        count: gapCounts.bg,
      }),
    mismatchCount > 0 &&
      t('page:adminPlantUnitMaster.unit.missing.partMismatch', {
        count: mismatchCount,
      }),
  ]
    .filter((part): part is string => part !== false)
    .join(f('missing.titleJoiner'))
  const gapDescriptionKey =
    gapCounts.plantCode === 0
      ? 'alertDescriptionBg'
      : gapCounts.bg === 0
        ? 'alertDescriptionPlantCode'
        : 'alertDescription'
  const noticeDescription = [
    hasGaps ? f(`missing.${gapDescriptionKey}`) : undefined,
    mismatchCount > 0 ? f('missing.mismatchDescription') : undefined,
  ]
    .filter((part): part is string => part !== undefined)
    .join('')

  return (
    <>
      {/* マスタ未設定 (系統コード / 所属 BG) は計画に載らないので、件数を絞り込みより上に出して気付けるようにする */}
      {hasNotice && (
        <CompactAlert
          description={noticeDescription}
          status="warning"
          title={noticeTitle}
        >
          {canFilterMissing && (
            <TertiaryButton
              flexShrink={0}
              onClick={() => setFilter({ ...filter, onlyMissingMaster: true })}
              size="sm"
            >
              {f('missing.showOnly')}
            </TertiaryButton>
          )}
        </CompactAlert>
      )}
      <MasterFilterToolbar
        afterArea={{
          key: 'generatorCategory',
          label: f('field.generatorCategory'),
          control: (
            <DsSelect
              width="160px"
              aria-label={f('field.generatorCategory')}
              onChange={(value) =>
                setFilter({
                  ...filter,
                  category:
                    value === undefined || value === ''
                      ? GeneratorCategory.UNSPECIFIED
                      : Number(value),
                })
              }
              options={[
                {
                  value: '',
                  label: t('page:adminPlantUnitMaster.unit.filter.all'),
                },
                ...GENERATOR_CATEGORY_OPTIONS.map((category) => ({
                  value: String(category),
                  label: generatorCategoryLabel(t, category),
                })),
              ]}
              value={
                filter.category === GeneratorCategory.UNSPECIFIED
                  ? ''
                  : String(filter.category)
              }
            />
          ),
          summaryValue:
            filter.category === GeneratorCategory.UNSPECIFIED
              ? t('page:adminPlantUnitMaster.unit.filter.all')
              : generatorCategoryLabel(t, filter.category),
          isModified: filter.category !== GeneratorCategory.UNSPECIFIED,
          onClear: () =>
            setFilter((prev) => ({
              ...prev,
              category: GeneratorCategory.UNSPECIFIED,
            })),
        }}
        extraFilters={[
          {
            key: 'other',
            label: f('filter.otherLabel'),
            control: (
              <HStack gap={4}>
                <Checkbox
                  checked={filter.includeRetired}
                  onCheckedChange={(details: { checked: boolean | string }) =>
                    setFilter({
                      ...filter,
                      includeRetired: details.checked === true,
                    })
                  }
                >
                  {f('filter.includeRetired')}
                </Checkbox>
                <Checkbox
                  checked={filter.onlyMissingMaster}
                  onCheckedChange={(details: { checked: boolean | string }) =>
                    setFilter({
                      ...filter,
                      onlyMissingMaster: details.checked === true,
                    })
                  }
                >
                  {f('filter.onlyMissing')}
                </Checkbox>
              </HStack>
            ),
            summaryValue: [
              ...(filter.includeRetired ? [f('filter.includeRetired')] : []),
              ...(filter.onlyMissingMaster ? [f('filter.onlyMissing')] : []),
            ].join('・'),
            isModified: filter.includeRetired || filter.onlyMissingMaster,
            onClear: () =>
              setFilter((prev) => ({
                ...prev,
                includeRetired: false,
                onlyMissingMaster: false,
              })),
          },
        ]}
        // クリアは同じクリックでエリア → 検索の順に呼ぶので、描画時点の filter を写すと後の更新が前を上書きする
        onSearchTextChange={(searchText) =>
          setFilter((prev) => ({ ...prev, searchText }))
        }
        onSelectArea={(areaCode) =>
          setFilter((prev) => ({ ...prev, areaCode }))
        }
        searchPlaceholder={f('searchPlaceholder')}
        searchText={filter.searchText}
        selectedAreaCode={filter.areaCode}
        storageKey={PLANT_UNIT_MASTER_FILTER_STORAGE_KEY}
      />
      <HStack
        justify="space-between"
        flexShrink={0}
        gap={2}
        marginTop={4}
        paddingTop={2}
        paddingBottom={2}
      >
        {/* 発電所ごとの配下ユニットの開閉 (見出し行のシェブロンで 1 発電所ずつ、ここで一括) */}
        <TertiaryButton
          onClick={toggleAllGroups}
          size="md"
          title={f('group.toggleAllHint')}
        >
          {f(allCollapsed ? 'group.expandAll' : 'group.collapseAll')}
        </TertiaryButton>
        <PrimaryButton
          leftIcon={<FaPlusRegular />}
          onClick={() => setCreateOpen(true)}
        >
          {t('page:adminPlantUnitMaster.add')}
        </PrimaryButton>
      </HStack>
      <RegionLoading
        flexDirection="column"
        flex="1"
        display="flex"
        minHeight={showInitialLoading ? '200px' : 0}
        isLoading={showInitialLoading}
      >
        {showInitialLoading ? null : (
          <PageScrollArea>
            <PlantUnitTable
              emptyLabel={f(unitRows.length === 0 ? 'empty' : 'filterEmpty')}
              errorLabel={errorLabel}
              isLoading={units.isLoading}
              onDelete={(unit) => {
                setDeleteError(undefined)
                setDeleteTarget(unit)
              }}
              onOpenDetail={openDetail}
              onSortChange={setSort}
              onToggleGroup={toggleGroup}
              plants={plantSources}
              rows={rows}
              showMissingBadges={hasGaps}
              siteAreas={siteAreas}
              siteAudits={siteAudits}
              sort={sort}
              today={today}
            />
          </PageScrollArea>
        )}
      </RegionLoading>
      {/* 条件 mount (閉じたら form ごと破棄): 次に開いた時に前回の入力が残らない。 */}
      {isCreateOpen && (
        <CreatePlantUnitModal
          isOpen
          onClose={() => setCreateOpen(false)}
          plants={plantRows}
          sites={siteRows}
          units={unitRows}
        />
      )}
      {deleteTarget !== undefined && (
        <DeleteConfirmDialog
          entityName={t('page:adminPlantUnitMaster.unit.deleteEntityName', {
            name: `${masters.sites.get(deleteTarget.generationSiteId)?.siteName ?? ''} ${deleteTarget.unitName}`.trim(),
          })}
          errorIsRetriable={deleteError?.retriable}
          errorMessage={deleteError?.message}
          isLoading={isDeleting}
          isOpen
          onCancel={closeDelete}
          onConfirm={() => {
            void confirmDelete()
          }}
          title={f('delete.confirmTitle')}
        />
      )}
    </>
  )
}
